import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './config/language';
import { ThemeProvider } from './config/theme';
import { AIChatProvider } from './config/aiChatContext';
import { CompareProvider } from './config/compareContext';
import { CartProvider } from './contexts/CartContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { AIChatWidget } from './components/AIChatWidget';
import CompareBar from './components/CompareBar';
import CompareModal from './components/CompareModal';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import MobileNumbers from './pages/MobileNumbers';
import BuyRequests from './pages/BuyRequests';
import ProductDetailsPage from './pages/ProductDetailsPage';
import SpareParts from './pages/SpareParts';
import CarPartsBrands from './pages/CarPartsBrands';
import CarPartsModels from './pages/CarPartsModels';
import CarPartsCategories from './pages/CarPartsCategories';
import Products from './pages/Products';
import Services from './pages/Services';
import TranslationServices from './pages/TranslationServices';
import DocumentServices from './pages/DocumentServices';
import CompanyFormation from './pages/CompanyFormation';
import CompanyFormationChat from './pages/CompanyFormationChat';
import VisaServices from './pages/VisaServices';
import ServiceDetail from './pages/ServiceDetail';
import ServiceChat from './pages/ServiceChat';
import Dashboard from './pages/Dashboard';
import AddPhoneListing from './pages/AddPhoneListing';
import AddVehicleListing from './pages/AddVehicleListing';
import AddPlateListing from './pages/AddPlateListing';
import ServicePackages from './pages/ServicePackages';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutUs from './pages/AboutUs';
import StorePage from './pages/StorePage';
import TermsOfService from './pages/TermsOfService';
import DataDeletion from './pages/DataDeletion';
import Support from './pages/Support';
import SearchPage from './pages/SearchPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import IdentityVerification from './pages/IdentityVerification';
import CarsPage from './pages/CarsPage';
import CarPlatesPage from './pages/CarPlatesPage';
import RealEstatePage from './pages/RealEstatePage';
import PhoneDetailsPage from './pages/PhoneDetailsPage';
import CarDetailsPage from './pages/CarDetailsPage';
import PlateDetailsPage from './pages/PlateDetailsPage';
import PropertyDetailsPage from './pages/PropertyDetailsPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import UnifiedPayPage from './pages/UnifiedPayPage';
import { initSessionGuard, isTokenExpired } from './lib/session';
import '@/App.css';

initSessionGuard();

// Layout wrapper that hides Navbar/Footer for chat pages
function LayoutWrapper({ children, user, onLogout }) {
  const location = useLocation();
  const isChatPage = location.pathname === '/services/chat';
  
  return (
    <>
      {!isChatPage && <Navbar user={user} onLogout={onLogout} />}
      {children}
      {!isChatPage && <Footer />}
      {!isChatPage && <CompareBar />}
      {!isChatPage && <CompareModal />}
      {!isChatPage && <AIChatWidget />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (userData && token && !isTokenExpired(token)) {
      setUser(JSON.parse(userData));
    } else if (userData || token) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    
    // Set initial direction based on saved language
    const savedLang = localStorage.getItem('cloudgm_lang') || 'ar';
    document.documentElement.setAttribute('dir', savedLang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', savedLang);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AIChatProvider>
          <CompareProvider>
            <CartProvider>
            <div className="App">
              <BrowserRouter>
                <ScrollToTop />
                <LayoutWrapper user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/real-estate" element={<Navigate to="/properties" replace />} />
                <Route path="/marketplace/real-estate" element={<Navigate to="/properties" replace />} />
                <Route path="/marketplace" element={<Navigate to="/marketplace/numbers" replace />} />
                <Route path="/marketplace/cars" element={<CarsPage />} />
                <Route path="/marketplace/numbers" element={<MobileNumbers />} />
                <Route path="/marketplace/plates" element={<CarPlatesPage />} />
                <Route path="/marketplace/car-plates" element={<CarPlatesPage />} />
                <Route path="/mobile-numbers" element={<MobileNumbers />} />
                <Route path="/market/numbers" element={<MobileNumbers />} />
                <Route path="/market/plates" element={<CarPlatesPage />} />
                <Route path="/market/cars" element={<CarsPage />} />
                <Route path="/cars" element={<CarsPage />} />
                <Route path="/plates" element={<CarPlatesPage />} />
                <Route path="/properties" element={<RealEstatePage />} />
                <Route path="/spare-parts" element={<CarPartsBrands />} />
                <Route path="/buy-requests" element={<BuyRequests />} />
                <Route path="/spare-parts/:brandId" element={<CarPartsModels />} />
                <Route path="/spare-parts/:brandId/:modelId" element={<CarPartsCategories />} />
                <Route path="/spare-parts/:brandId/:modelId/:categoryId" element={<SpareParts />} />
                <Route path="/products" element={<Products />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/translation" element={<TranslationServices />} />
                <Route path="/services/documents" element={<DocumentServices />} />
                <Route path="/services/company-formation" element={<CompanyFormation />} />
                <Route path="/services/company-formation/chat" element={<CompanyFormationChat />} />
                <Route path="/services/visa" element={<VisaServices />} />
                <Route path="/services/visa/:serviceId" element={<ServiceDetail />} />
                <Route path="/services/chat" element={<ServiceChat />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/ads/new-phone" element={<AddPhoneListing />} />
                <Route path="/dashboard/ads/new-vehicle" element={<AddVehicleListing />} />
                <Route path="/dashboard/ads/new-plate" element={<AddPlateListing />} />
                <Route path="/dashboard/ads/edit/phones/:id" element={<AddPhoneListing />} />
                <Route path="/dashboard/ads/edit/vehicles/:id" element={<AddVehicleListing />} />
                <Route path="/dashboard/ads/edit/plates/:id" element={<AddPlateListing />} />
                <Route path="/service-packages" element={<ServicePackages />} />
                <Route path="/verify-identity" element={<IdentityVerification />} />
                <Route path="/verification-callback" element={<IdentityVerification />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/store/:storeId" element={<StorePage />} />
                <Route path="/phone/:id" element={<PhoneDetailsPage />} />
                <Route path="/car/:id" element={<CarDetailsPage />} />
                <Route path="/plate/:id" element={<PlateDetailsPage />} />
                <Route path="/property/:id" element={<PropertyDetailsPage />} />
                <Route path="/product/:id" element={<ProductDetailsPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/pay" element={<UnifiedPayPage />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="/support" element={<Support />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/pay/success" element={<PaymentSuccess />} />
                <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                <Route path="/payment/cancel" element={<PaymentCancelled />} />
                <Route path="/payment/cancelled" element={<PaymentCancelled />} />
                <Route path="/pay/cancel" element={<PaymentCancelled />} />
              </Routes>
                </LayoutWrapper>
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </div>
            </CartProvider>
          </CompareProvider>
        </AIChatProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
